import { PROBLEM_TYPES } from '@rheora/enrollment-contract';

/**
 * Classifying WHY `POST /bff/credit/check` refused, so the credit screen can name
 * the field to fix instead of one dead end.
 *
 * ── THE DEFECT THIS CLOSES ─────────────────────────────────────────────────
 * Every non-501 failure rendered the same alert — "Something went wrong. Please
 * try again." Accounts refuses a structurally invalid SSN/ITIN before any bureau
 * call, and since `108afea` the BFF says so with its own status and code; the
 * storefront was not listening. For a customer who fat-fingered a digit "please
 * try again" is FALSE in exactly the way the three ESIID refusals were false
 * before they got their own types — retrying the same number cannot work.
 *
 * ── WHY BRANCH ON `type` AND NOT ON THE STATUS ─────────────────────────────
 * `identity-invalid` and `bad-request` are both 400s with different remedies, so
 * the status cannot tell them apart. `PROBLEM_TYPES` is the SHARED contract both
 * sides import — the storefront cannot drift from the BFF's vocabulary without a
 * compile error, which a hand-copied string literal would happily allow.
 *
 * ── WHAT THE STOREFRONT DELIBERATELY DOES NOT LEARN ────────────────────────
 * ⚠️ Only THAT the number was unreadable, never WHY. Accounts distinguishes a
 * malformed number from a forbidden area from a kind mismatch; the BFF keeps
 * that in its operator log and does not put it on the wire, because "invalid
 * format" and "failed verification" are different answers about a real person's
 * identity and a caller who can tell them apart has an enumeration oracle. This
 * module could not surface the distinction if it wanted to, and must not grow a
 * branch that tries.
 *
 * ── WHY THE COPY IS NOT READ OFF THE WIRE ──────────────────────────────────
 * Same rule as `quote-draft-error.service.ts`: the problem body's `detail` is
 * English prose in an operator-readable envelope. Rendering it would put an
 * untranslated, unversioned server string in front of the customer and bypass
 * `t()`. The wire carries the DISCRIMINATOR; the storefront owns the words.
 */

/**
 * What actually went wrong, in terms the credit screen can act on.
 *
 * `unknown` is deliberately retained and deliberately last: an unrecognised
 * failure — an outage, a network drop, a problem type this build predates — keeps
 * the existing generic alert rather than being forced into whichever branch reads
 * closest. A wrong-but-specific explanation is worse than an honest vague one.
 *
 * ⚠️ THIS IS ALSO WHAT MAKES THE CONTRACT ADDITION SAFE IN BOTH DIRECTIONS. A
 * cached bundle built before `identityInvalid` existed falls through to
 * `unknown` and behaves exactly as it does today.
 */
export type CreditFailure = 'identity-invalid' | 'identity-number-required' | 'unknown';

/**
 * Classify a credit-check refusal from its problem `type`.
 *
 * Takes the bare URN (read by `problemTypeOf` in the api layer) rather than the
 * error object, so this module stays pure and has no dependency on the network
 * seam — the classification is a domain rule, not a transport concern.
 *
 * @param problemType the BFF's RFC7807 `type`, or `undefined` when the failure
 *   carried none (a transport drop, a thrown non-`BffError`). Unrecognised and
 *   absent both classify as `unknown`, which keeps the existing generic alert.
 */
export function classifyCreditFailure(problemType: string | undefined): CreditFailure {
  switch (problemType) {
    case PROBLEM_TYPES.identityInvalid:
      return 'identity-invalid';
    case PROBLEM_TYPES.identityNumberRequired:
      return 'identity-number-required';
    default:
      return 'unknown';
  }
}

/**
 * True when the refusal was about the identity number the customer typed.
 *
 * The named predicate the screen reads, so no component has to know the URN. It
 * lives here rather than beside `isNotImplemented` in `api/bff-client.ts`
 * because that module's own note says `problemTypeOf` returns a bare string
 * precisely to keep classification in `services/`, pure and free of any
 * API-layer type — and because `isNotImplemented` branches on STATUS, which is
 * exactly what cannot separate these two 400s.
 */
export function isIdentityInvalid(problemType: string | undefined): boolean {
  return classifyCreditFailure(problemType) === 'identity-invalid';
}

/** The i18n keys the alert renders for a classified failure. */
export interface CreditFailureCopy {
  readonly titleKey: string;
  readonly bodyKey: string;
}

/**
 * The copy keys for a failure. Pure key selection — no strings, no formatting, so
 * the component stays render-only and every word still goes through `t()`.
 *
 * The `unknown` branch keeps the ORIGINAL generic keys, unchanged. That is the
 * point: this adds a specific answer where we have one, and does not quietly
 * relabel every remaining failure as something we understand.
 */
export function creditFailureCopy(failure: CreditFailure): CreditFailureCopy {
  switch (failure) {
    case 'identity-invalid':
      return {
        titleKey: 'shop.credit.error.identityInvalid.title',
        bodyKey: 'shop.credit.error.identityInvalid.body',
      };
    case 'identity-number-required':
      return {
        titleKey: 'shop.credit.error.identityNumberRequired.title',
        bodyKey: 'shop.credit.error.identityNumberRequired.body',
      };
    case 'unknown':
      return {
        titleKey: 'shop.credit.error.title',
        bodyKey: 'shop.credit.error.body',
      };
  }
}

/**
 * The DOM id of the field a failure should send the customer to, or `null` when
 * the failure names no field.
 *
 * ── WHY FOCUS AND NOT JUST COPY ────────────────────────────────────────────
 * An alert that says "check the number you entered" is only useful if the
 * customer can find it. A screen-reader user hears the alert at the bottom of a
 * long form and has no way to know which of six inputs it means; moving focus IS
 * the answer for them, and it costs a sighted user nothing.
 *
 * Returning an id rather than a ref keeps this pure and testable — the component
 * owns the DOM.
 */
export function creditFailureFocusFieldId(failure: CreditFailure): string | null {
  switch (failure) {
    case 'identity-invalid':
      return 'cr-ssn';
    // The SSN field for this one too, and for a sharper reason: the refusal IS
    // "there is no SSN here". The customer filled in a driver licence and is being
    // asked for the one field they left blank, so sending them anywhere else would
    // be sending them away from the answer.
    case 'identity-number-required':
      return 'cr-ssn';
    case 'unknown':
      return null;
  }
}
